import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, Stack, Chip, useMediaQuery } from '@mui/material';
import Slider from 'react-slick';
import { styled } from '@mui/material/styles';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getUnitType, StyledTitleSection } from '../projectDetailSection/ProjectDetailContent';
import { useTranslation } from 'next-i18next';
import { grey } from '@mui/material/colors';
import Image from 'next/image';
import FallbackImage from '../FallbackImage';
import UnitIcon from '../Icon/UnitIcon';
import styles from './ProjectCarousel.module.scss';

export const StyledSlider = styled(Slider)(({ theme }) => ({
  '.slick-dots': {
    bottom: '-40px',
  },
  '.slick-dots li': {
    margin: '0 2px',
  },
  '.slick-dots li button': {
    backgroundColor: grey[300],
    borderRadius: '50%',
    width: '8px',
    height: '8px',
    padding: 0,
    '&:before': {
      content: '""',
    },
  },
  '.slick-dots li.slick-active button': {
    backgroundColor: theme.palette.primary.main,
  },
  '.slick-prev, .slick-next': {
    display: 'none !important',
  },
}));

export const sliderSettings = {
  dots: true,
  infinite: false,
  speed: 500,
  arrows: false,
  centerMode: false,
  variableWidth: false,
  slidesToShow: 3,
  slidesToScroll: 3,
  responsive: [
    {
      breakpoint: 834,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    },
    {
      breakpoint: 525,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

export const buildProjectHref = (project: any, locale, t): string => {
  let href = `/${t('project')}/${t(project.category)}/${project.id}/${project.title}`;
  if (locale?.toLowerCase() !== 'th') href = `/${locale}${href}`;
  return href;
};

export const ProjectCard = ({ project, onClick, ...props }) => {
  const { t } = useTranslation();

  const unitTypeMap = useMemo(() => getUnitType(t), [t]);

  const units = useMemo(() => {
    return project?.units?.map((item) => ({
      id: item.unit_ID,
      type: item.unit_Type ? unitTypeMap[item.unit_Type] : '',
      series: item.unit_Series,
      size: item.unit_Sqm || '-',
      unit: t('square meter'),
    }));
  }, [project]);

  if (!project) return null;

  return (
    <Card
      sx={{
        borderRadius: 2,
        height: '100%',
        mx: 0.7,
        boxShadow: 'none',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0px 2px 10px 0px #0000001A',
        },
        ...props,
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: 225,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <FallbackImage
          src={project.image}
          alt={project.title}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 360px"
          loading="lazy"
        />
      </Box>
      <CardContent style={{ padding: 15 }}>
        <Typography
          variant="h4"
          fontWeight={500}
          gutterBottom
          sx={{
            fontSize: '1rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {project.title}
        </Typography>

        {project.tags?.length > 0 && (
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            mt={1}
            sx={{
              overflow: 'hidden',
              maxHeight: 24,
            }}
          >
            {project.tags?.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                variant="filled"
                sx={{
                  px: 0.5,
                  borderRadius: 1.5,
                  color: 'grey.800',
                  bgcolor: 'grey.100',
                  whiteSpace: 'nowrap',
                  lineHeight: '28px',
                }}
              />
            ))}
          </Stack>
        )}

        <Box display={'flex'} width={'auto'} sx={{ fontSize: '0.8rem', fontWeight: 500, gap: 1, mt: 1 }}>
          <Box
            sx={{
              display: 'flex',
              bgcolor: '#FEF1EA',
              borderRadius: 2,
              width: '100%',
              justifyContent: 'center',
              py: 0.75,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 1,
                width: '100%',
                textAlign: 'center',
              }}
            >
              {t('sell')}{' '}
              <Box>
                {project.countSell || 0} {t('listing')}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              bgcolor: '#F0F7E8',
              borderRadius: 2,
              width: '100%',
              justifyContent: 'center',
              py: 0.75,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 1,
                width: '100%',
                textAlign: 'center',
              }}
            >
              {t('rent')}
              <Box>
                {project.countRent || 0} {t('listing')}
              </Box>
            </Box>
          </Box>
        </Box>

        <Typography variant="body2" color={'grey.800'} mt={1}>
          {t('price-start-short')}
          <Box
            component={'span'}
            sx={{
              fontSize: 16,
              color: 'primary.main',
              fontWeight: 500,
            }}
          >
            {' '}
            {project.price?.toLocaleString() || '-'}{' '}
          </Box>
          {t('THB')}
        </Typography>

        <Typography variant="body2" color={'grey.800'}>
          {t('rent-start-short')}
          <Box
            component={'span'}
            sx={{
              fontSize: 16,
              color: 'primary.main',
              fontWeight: 500,
            }}
          >
            {' '}
            {project.rentPrice?.toLocaleString() || '-'}{' '}
          </Box>
          {t('thb-per-month')}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1} mt={1}>
          <UnitIcon width={40} height={40} />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              justifyContent: 'center',
              height: '3rem',
              pl: 0.5,
            }}
          >
            {units?.length > 0 ? (
              <Fragment>
                {units?.slice(0, 3).map((item, index) => {
                  const isLastShown = index === 2 && units.length > 3;
                  return (
                    <Typography
                      key={index}
                      variant="body2"
                      noWrap
                      sx={{
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '0.7rem',
                      }}
                    >
                      {`${item.type} ${item.series} ${item.size} ${item.unit}`}
                      {isLastShown && ' >'}
                    </Typography>
                  );
                })}
              </Fragment>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.7rem',
                  }}
                >
                  {t('no-information')}
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>

        <Stack direction="row" gap={1} alignItems="center" mt={1}>
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
            }}
          >
            <Image
              src="/assets/googlemap.svg"
              alt="Google Map"
              width={44}
              height={44}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="caption"
              color={'grey.800'}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: '125%',
              }}
            >
              {project.location}
            </Typography>

            {project.nearbyLocation?.distance && project.nearbyLocation?.location && (
              <Typography
                variant="caption"
                color="grey.600"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: '125%',
                }}
              >
                <Box component="span" sx={{ color: 'primary.main' }}>{project.nearbyLocation?.distance?.split(' ')[0]}</Box>{' '}
                {`${project.nearbyLocation?.distance?.split(' ')[1]} ${t('to')}`}{' '}
                <Box component="span" sx={{ color: 'primary.main' }}>{project.nearbyLocation?.location}</Box>
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ProjectSlider = ({ title, projects, locale }) => {
  const { t } = useTranslation();
  const [sliderKey, setSliderKey] = useState(0);
  const isMobile = useMediaQuery('(max-width:525px)');
  const isTablet = useMediaQuery('(max-width:834px)');

  const maxSlidesToShow = useMemo(() => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    return 3;
  }, [isMobile, isTablet]);
  const useSlick = projects.length > maxSlidesToShow;

  useEffect(() => {
    const handleResize = () => {
      setSliderKey((prev) => prev + 1);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return projects?.length > 0 && (
    <Box my={4} mt={7}>
      <StyledTitleSection variant="h3" gutterBottom>
        {title}
      </StyledTitleSection>

      <Box className={styles.carouselWrapper}>
        {useSlick ? (
          <StyledSlider key={sliderKey} {...sliderSettings}>
            {projects?.map((project, idx) => (
              <Box key={idx} sx={{ px: isMobile ? 3 : 0.3 }}>
                <ProjectCard
                  project={project}
                  onClick={() => window.open(buildProjectHref(project, locale, t), '_self')}
                />
              </Box>
            ))}
          </StyledSlider>
        ) : (
          <Box
            sx={{
              display: 'flex',
              py: '10px',
            }}
          >
            {projects?.map((project, idx) => (
              <Box
                key={idx}
                sx={{
                  scrollSnapAlign: 'start',
                  flex: `0 1 calc(100% / ${maxSlidesToShow})`,
                  maxWidth: `calc(100% / ${maxSlidesToShow})`,
                  px: isMobile ? 3 : 0.3,
                }}
              >
                <ProjectCard
                  project={project}
                  onClick={() => window.open(buildProjectHref(project, locale, t), '_self')}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProjectSlider;
